import { AlertCircle, Calendar, Zap } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getWorkTask } from '../../api/workApi'
import type { WorkTask } from '../../types/work'
import WorkDownloadButton from './WorkDownloadButton'
import WorkStatusBadge from './WorkStatusBadge'

interface WorkTaskCardProps {
  task: WorkTask
  onRefresh: (updated: WorkTask) => void
}

const CATEGORY_LABELS: Record<string, string> = {
  audio_processing: 'Audio',
  image_generation: 'Image',
  content_writing: 'Content',
  code: 'Code',
  data_analysis: 'Data',
}

const SUBCATEGORY_LABELS: Record<string, string> = {
  transcription: 'Transcription',
  translation: 'Translation',
  tts: 'Text to Speech',
  image_gen: 'Image Generation',
  image_analysis: 'Image Analysis',
  content_gen: 'Content Generation',
  summarization: 'Summarization',
  code_gen: 'Code Generation',
  embedding: 'Embedding',
  sentiment: 'Sentiment Analysis',
}

export default function WorkTaskCard({ task, onRefresh }: WorkTaskCardProps) {
  const navigate = useNavigate()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Poll every 3s while processing
  useEffect(() => {
    if (task.status !== 'processing' && task.status !== 'pending') {return}

    function poll() {
      void (async () => {
        try {
          const updated = await getWorkTask(task.id)
          onRefresh(updated)
          if (updated.status !== 'processing' && updated.status !== 'pending') {
            if (intervalRef.current) { clearInterval(intervalRef.current) }
          }
        } catch {
          // silent
        }
      })()
    }

    intervalRef.current = setInterval(poll, 3000)

    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current) }
    }
  }, [task.id, task.status, onRefresh])

  const createdDate = new Date(task.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div
      onClick={() => navigate(`/work/${task.id}`)}
      style={{
        padding: '16px',
        borderRadius: '12px',
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary, #6366f1)'
        ;(e.currentTarget as HTMLDivElement).style.boxShadow = '0 0 0 1px var(--primary, #6366f1)'
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-color)'
        ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.title}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)', marginTop: '2px' }}>
            {CATEGORY_LABELS[task.category] ?? task.category} · {SUBCATEGORY_LABELS[task.subcategory] ?? task.subcategory}
          </div>
        </div>
        <WorkStatusBadge status={task.status} />
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: 'var(--primary, #6366f1)',
              fontWeight: 600,
            }}
          >
            <Zap size={12} />
            {task.flz_cost.toFixed(2)} FLZ
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            <Calendar size={11} />
            {createdDate}
          </span>
        </div>

        {/* Download button for completed tasks */}
        {task.status === 'completed' && task.download_url && task.download_expires_at && (
          <WorkDownloadButton
            taskId={task.id}
            expiresAt={task.download_expires_at}
            downloadUrl={task.download_url}
          />
        )}

        {/* Error message for failed tasks */}
        {task.status === 'failed' && task.error_message && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              color: 'var(--danger, #ef4444)',
            }}
          >
            <AlertCircle size={12} />
            {task.error_message.slice(0, 60)}
          </div>
        )}
      </div>
    </div>
  )
}
